import { useEffect, useState } from 'react';

interface NavigatorUAData {
  mobile?: boolean;
}

const MOBILE_POINTER_QUERY = '(pointer: coarse) and (hover: none)';

/**
 * Phones, tablets and other touch-primary handhelds. Intentionally narrow:
 * touchscreen laptops report a fine primary pointer and are treated as desktop.
 */
const MOBILE_UA_REGEX =
  /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet|Silk/i;

/**
 * Detect whether the current device is a mobile/tablet handheld.
 *
 * Combines several signals so it stays reliable across browsers and devices,
 * including iPadOS Safari (which reports a desktop User-Agent). First positive
 * signal wins:
 *
 * 1. `navigator.userAgentData.mobile` — explicit, spoof-resistant (Chromium).
 * 2. `(pointer: coarse) and (hover: none)` — touch-primary, hover-less devices
 *    (phones + tablets, incl. iPadOS); touchscreen laptops keep a fine primary
 *    pointer so they stay desktop.
 * 3. iPadOS masquerading as macOS — `MacIntel` platform with multi-touch.
 * 4. User-Agent regex — fallback for browsers lacking the above.
 */
export function detectMobileDevice(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return false;
  }

  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData })
    .userAgentData;
  if (uaData?.mobile === true) {
    return true;
  }

  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia(MOBILE_POINTER_QUERY).matches
  ) {
    return true;
  }

  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    return true;
  }

  return MOBILE_UA_REGEX.test(navigator.userAgent);
}

/**
 * React hook returning `true` on mobile/tablet devices.
 *
 * SSR/SSG-safe: returns `false` during server render and the initial client
 * render to avoid hydration mismatches, then resolves to the real value after
 * mount. Re-evaluates if the primary pointer capability changes.
 *
 * @example
 * ```tsx
 * const isMobile = useIsMobileDevice();
 * return isMobile ? <SignUpButton /> : <DownloadButton />;
 * ```
 */
export function useIsMobileDevice(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(detectMobileDevice());

    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(MOBILE_POINTER_QUERY);
    const onChange = () => setIsMobile(detectMobileDevice());
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}
